import React, { useState, useEffect } from "react";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Button, TextField, Typography, Tooltip } from "@mui/material";
import api from "../../API/axios";

// Helper function to calculate age from date of birth
const calculateAge = (dobString: string): number => {
  const dob = new Date(dobString);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
};

interface PendingOAuthData {
  provider: "google" | "apple" | "facebook";
  email: string;
  name?: string;
  token?: string; // For Apple - the identity token
  code?: string; // For Google/Facebook - the authorization code (deprecated)
  oauthSessionToken?: string; // New: session token for completing OAuth registration
  clientId: number;
}

interface CollectDobProps {
  config: any;
  pendingOAuthData: PendingOAuthData;
  onAuthSuccess: (data: any) => void;
  onStateChange: (state: string) => void;
  setConsumerToken?: (token: string) => void;
  showSnackbar?: (message: string, severity: "success" | "error" | "warning" | "info") => void;
  onCancel?: () => void;
}

const CollectDob: React.FC<CollectDobProps> = ({
  config,
  pendingOAuthData,
  onAuthSuccess,
  onStateChange,
  setConsumerToken,
  showSnackbar,
  onCancel,
}) => {
  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [minimumAge, setMinimumAge] = useState(18);

  // Fetch minimum age configuration from backend
  useEffect(() => {
    const fetchMinimumAge = async () => {
      try {
        const response = await api.get("/config/analytics");
        const configuredAge = response.data?.minimumAge;
        if (configuredAge && typeof configuredAge === "number" && configuredAge > 0) {
          setMinimumAge(configuredAge);
        }
      } catch (err) {
        console.error("Failed to fetch age config, using default 18:", err);
      }
    };
    fetchMinimumAge();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    // Validate DOB
    if (!dob) {
      const errorMsg = "Please enter your date of birth.";
      setError(errorMsg);
      showSnackbar?.(errorMsg, "error");
      return;
    }

    // Validate age
    const age = calculateAge(dob);
    if (age < minimumAge) {
      const errorMsg = `You must be at least ${minimumAge} years old to register.`;
      setError(errorMsg);
      showSnackbar?.(errorMsg, "error");
      return;
    }

    setLoading(true);

    try {
      let response;
      const provider = pendingOAuthData.provider;

      // Use the new unified endpoint if we have an oauthSessionToken
      if (pendingOAuthData.oauthSessionToken) {
        response = await api.post("/consumer/auth/complete-oauth-registration", {
          oauthSessionToken: pendingOAuthData.oauthSessionToken,
          dateOfBirth: dob,
        });
      } else if (provider === "google" && pendingOAuthData.code) {
        // Legacy fallback - should not normally be used
        response = await api.post("/consumer/auth/google/callback", {
          code: pendingOAuthData.code,
          clientId: pendingOAuthData.clientId,
          mode: "register",
          dateOfBirth: dob,
        });
      } else if (provider === "apple") {
        // Call Apple auth endpoint with DOB
        response = await api.post("/consumer/auth/apple", {
          token: pendingOAuthData.token,
          clientId: pendingOAuthData.clientId,
          mode: "register",
          dateOfBirth: dob,
        });
      } else if (provider === "facebook" && pendingOAuthData.code) {
        // Legacy fallback - should not normally be used
        response = await api.post("/consumer/auth/facebook/callback", {
          code: pendingOAuthData.code,
          clientId: pendingOAuthData.clientId,
          mode: "register",
          dateOfBirth: dob,
        });
      } else {
        throw new Error("Missing OAuth session data. Please try signing up again.");
      }

      const data = response.data;
      console.log("✅ OAuth registration completed with DOB:", data);

      // Store token
      if (setConsumerToken && data.token) {
        setConsumerToken(data.token);
      } else if (data.token) {
        localStorage.setItem(`consumerToken_${pendingOAuthData.clientId}`, data.token);
      }

      // Clear pending OAuth data from localStorage
      localStorage.removeItem("pendingOAuthData");

      // Success - navigate to authenticated state
      showSnackbar?.("Registration successful!", "success");
      onAuthSuccess({
        isAuthenticated: true,
        consumerId: data.consumerId,
        email: data.email,
        clientId: data.clientId || pendingOAuthData.clientId,
        requiresTwoFactor: false,
        credits: data.credits,
      });
    } catch (err: any) {
      console.error("❌ OAuth registration with DOB failed:", err);
      let errorMsg = "Registration failed. Please try again.";
      if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
      showSnackbar?.(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Clear pending OAuth data
    localStorage.removeItem("pendingOAuthData");
    if (onCancel) {
      onCancel();
    } else {
      onStateChange("register");
    }
  };

  return (
    <div className="register-container">
      <div style={{ marginBottom: 16 }}>
        <Typography variant="h6" component="h2" sx={{ marginBottom: 2, textAlign: "center" }}>
          Complete Registration
        </Typography>
        {pendingOAuthData.email && (
          <Typography variant="body2" sx={{ marginBottom: 2, textAlign: "center", color: "#666" }}>
            Signing up as: <strong>{pendingOAuthData.email}</strong>
          </Typography>
        )}
        <Typography variant="body2" sx={{ textAlign: "center", color: "#666" }}>
          Please enter your date of birth to continue.
        </Typography>
      </div>

      <form onSubmit={handleSubmit}>
        {error && <div className="error-message">{error}</div>}

        <div style={{ width: "100%", marginBottom: 24 }}>
          <TextField
            type="date"
            label="Date of Birth"
            name="dob"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            fullWidth
            required
            disabled={loading}
            inputProps={{
              min: "1900-01-01",
              max: (() => {
                const d = new Date();
                return d.toISOString().split("T")[0];
              })(),
            }}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              width: "100%",
              marginBottom: 1,
            }}
          />
          <Tooltip
            title="Our services are age restricted and require age verification."
            arrow
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: "#000",
                  color: "#fff",
                  fontSize: "12px",
                  padding: "8px 12px",
                  "& .MuiTooltip-arrow": {
                    color: "#000",
                  },
                },
              },
            }}
          >
            <span
              style={{
                cursor: "pointer",
                color: "#666",
                fontSize: "12px",
                display: "inline-flex",
                alignItems: "center",
                marginTop: "4px",
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 14, marginRight: "4px" }} />
              Why do we need this?
            </span>
          </Tooltip>
        </div>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{
            width: "100%",
            height: "42px",
            backgroundColor: "var(--color-primary)",
            "&:hover": { backgroundColor: "var(--color-secondary)" },
            marginBottom: 2,
            textTransform: "none",
            fontWeight: 500,
          }}
          disabled={loading}
        >
          {loading ? "Completing Registration..." : "Continue"}
        </Button>

        <Button
          type="button"
          variant="outlined"
          fullWidth
          onClick={handleCancel}
          sx={{
            width: "100%",
            height: "42px",
            color: "var(--color-primary)",
            borderColor: "var(--color-primary)",
            "&:hover": {
              backgroundColor: "rgba(171, 44, 41, 0.04)",
              borderColor: "var(--color-secondary)",
              color: "var(--color-secondary)",
            },
            textTransform: "none",
            fontWeight: 500,
          }}
          disabled={loading}
        >
          Cancel
        </Button>
      </form>
    </div>
  );
};

export default CollectDob;
